import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { PDFData } from '../data/pdf-data';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class PdfService {
    constructor(
        private translate: TranslateService,
        private datePipe: DatePipe
    ) {}
    generatePdf(reportData: any, caseId: string) {
        const doc = new jsPDF();
        const { pcaseloan } = reportData;

        // Set page margins
        const margins = {
            top: 20,
            bottom: 20,
            left: 15,
            right: 15,
        };

        let currentY = margins.top;

        // Set default font
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);

        // Header Section - Company Name and Registration
        const companyName = this.getPropertyValue(
            pcaseloan,
            'cCaseBizName',
            '-'
        );
        const regNo = this.getPropertyValue(pcaseloan, 'cCaseBizRegNo', '-');
        const newRegNo = this.getPropertyValue(
            pcaseloan,
            'cCaseBusregNew',
            'Not available'
        );

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${companyName}`, margins.left, currentY);
        currentY += 6;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(
            `(Reg No: ${regNo} | New Reg No: ${newRegNo})`,
            margins.left,
            currentY
        );
        currentY += 6;

        // Created on and Application info
        const createdOn = moment(
            this.getPropertyValue(pcaseloan, 'createdDateTime')
        ).format('YYYY-MM-DD HH:mm:ss');
        const applicationNo = this.getPropertyValue(
            pcaseloan,
            'cCaseAppNo',
            '-'
        );
        doc.text(
            `Created on: ${createdOn} | Application no: ${applicationNo}`,
            margins.left,
            currentY
        );
        currentY += 6;

        // Product
        const product = this.getPropertyValue(pcaseloan, 'productName', '-');
        doc.text(`Product: ${product}`, margins.left, currentY);
        currentY += 6;

        // Divider line
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.line(margins.left, currentY, pageWidth - margins.right, currentY);
        currentY += 6;

        // Basic Info Table
        autoTable(doc, {
            startY: currentY,
            theme: 'plain',
            margin: { left: margins.left, right: margins.right },
            styles: {
                fontSize: 10,
                cellPadding: 1,
                lineWidth: 0,
            },
            columnStyles: {
                0: { cellWidth: 95 },
                1: { cellWidth: 95 },
            },
            body: [
                [
                    `FINANCING AMOUNT (RM): ${this.formatCurrency(
                        this.getPropertyValue(pcaseloan, 'cCaseFinamt', 0)
                    )}`,
                    `TENURE (YEAR): ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseTenure',
                        '-'
                    )}`,
                ],
                [
                    `Applicant Name: ${this.capitalizeFirst(
                        this.getPropertyValue(pcaseloan, 'cCaseAppName', '-')
                    )}`,
                    `APPLICANT IC NO.: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseNric',
                        '-'
                    )}`,
                ],
                [
                    `EMAIL: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseEmail',
                        '-'
                    )}`,
                    `MOBILE: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseMobile',
                        '-'
                    )}`,
                ],
            ],
        });

        currentY = (doc as any).lastAutoTable.finalY + 6;

        // Applicant Info Section
        currentY = this.checkPageBreak(doc, currentY, margins, 50); // Check if we need a new page::KSK
        currentY = this.addSectionHeader(doc, 'Applicant Info', currentY);
        autoTable(doc, {
            startY: currentY,
            theme: 'plain',
            margin: { left: margins.left, right: margins.right },
            styles: {
                fontSize: 10,
                cellPadding: 1,
                lineWidth: 0,
            },
            columnStyles: {
                0: { cellWidth: 95 },
                1: { cellWidth: 95 },
            },
            body: [
                [
                    `COMPANY NAME: ${companyName}`,
                    `BUSINESS REGISTRATION NO.: ${regNo}`,
                ],
                [
                    `NEW BUSINESS REGISTRATION NO.: ${newRegNo}`,
                    `BUSINESS CONSTITUTION: ${this.getMappedLabel(
                        'bizEntityType',
                        this.getPropertyValue(pcaseloan, 'cCaseBizConst', '-')
                    )}`,
                ],
                [
                    `YEARS IN OPERATION: ${this.getMappedLabel(
                        'bizOpYear',
                        this.getPropertyValue(pcaseloan, 'cCaseBizOpYear', '-')
                    )}`,
                    `BUMI STATUS: ${
                        this.getPropertyValue(pcaseloan, 'cCaseBumi') === 'Y'
                            ? 'Yes'
                            : 'No'
                    }`,
                ],
                [
                    `BUSINESS SECTOR: ${this.getMappedLabel(
                        'bizSector',
                        this.getPropertyValue(pcaseloan, 'cCaseBizSector', '-')
                    )}`,
                    `BUSINESS REGISTERED IN SABAH/SARAWAK?: ${
                        this.getPropertyValue(pcaseloan, 'cCaseMalaysian') ===
                        'Y'
                            ? 'Yes'
                            : 'No'
                    }`,
                ],
                [
                    `SHARIAH COMPLIANCE PRODUCT PREFERENCE: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseShariah',
                        '-'
                    )}`,
                    '',
                ],
            ],
        });

        currentY = (doc as any).lastAutoTable.finalY + 6;

        // Management Info Section
        currentY = this.checkPageBreak(doc, currentY, margins, 50); // Check if we need a new page
        currentY = this.addSectionHeader(doc, 'Management Info', currentY);
        autoTable(doc, {
            startY: currentY,
            theme: 'plain',
            margin: { left: margins.left, right: margins.right },
            styles: {
                fontSize: 10,
                cellPadding: 1,
                lineWidth: 0,
            },
            body: [
                [
                    `Applicant Name: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseAppName',
                        '-'
                    )}`,
                ],
                [
                    `Applicant IC No.: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseNric',
                        '-'
                    )}`,
                ],
                [
                    `Directors Age: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseMgtAge',
                        '-'
                    )}`,
                ],
            ],
        });

        currentY = (doc as any).lastAutoTable.finalY + 6;

        // Business Address Info Section
        currentY = this.checkPageBreak(doc, currentY, margins, 50); // Check if we need a new page
        currentY = this.addSectionHeader(
            doc,
            'Business Address Info',
            currentY
        );
        autoTable(doc, {
            startY: currentY,
            theme: 'plain',
            margin: { left: margins.left, right: margins.right },
            styles: {
                fontSize: 10,
                cellPadding: 1,
                lineWidth: 0,
            },
            body: [
                [
                    `ADDRESS (LINE 1): ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseAddr1',
                        '-'
                    )}`,
                ],
                [
                    `ADDRESS (LINE 2): ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseAddr2',
                        '-'
                    )}`,
                ],
                [
                    `POSTCODE: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseAddrPostCode',
                        '-'
                    )}`,
                ],
                [
                    `CITY: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseAddrTown',
                        '-'
                    )}`,
                ],
                [
                    `STATE: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseAddrState',
                        '-'
                    )}`,
                ],
            ],
        });

        currentY = (doc as any).lastAutoTable.finalY + 6;

        // Special Request Section
        currentY = this.checkPageBreak(doc, currentY, margins, 30); // Check if we need a new page
        currentY = this.addSectionHeader(doc, 'Special Request', currentY);
        currentY += 2; // Add spacing between header and content
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text('Not Available', margins.left, currentY);
        currentY += 6;

        // Product Change Section
        currentY = this.checkPageBreak(doc, currentY, margins, 30); // Check if we need a new page
        currentY = this.addSectionHeader(
            doc,
            'Product Change(if any)',
            currentY
        );
        currentY += 2; // Add spacing between header and content
        doc.text('New Product Not Available', margins.left, currentY);
        currentY += 6;

        // Financial Info Section
        currentY = this.checkPageBreak(doc, currentY, margins, 50); // Check if we need a new page
        currentY = this.addSectionHeader(doc, 'Financial Info', currentY);
        autoTable(doc, {
            startY: currentY,
            theme: 'plain',
            margin: { left: margins.left, right: margins.right },
            styles: {
                fontSize: 10,
                cellPadding: 1,
                lineWidth: 0,
            },
            body: [
                [
                    `FINANCING AMOUNT (RM): ${this.formatCurrency(
                        this.getPropertyValue(pcaseloan, 'cCaseFinamt', 0)
                    )}`,
                ],
                [
                    `PURPOSE OF FINANCING: ${this.getMappedLabel(
                        'loanType',
                        this.getPropertyValue(pcaseloan, 'cCasePof', '-')
                    )}`,
                ],
                [
                    `TENURE (YEARS): ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseTenure',
                        '-'
                    )}`,
                ],
                [
                    `BRANCH: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseCgcBranch',
                        'No'
                    )}`,
                ],
                [
                    `APPROVED AMOUNT (RM): ${this.formatCurrency(
                        this.getPropertyValue(pcaseloan, 'cCaseApproveAmt', '-')
                    )}`,
                ],
                [
                    `REJECT REASON: ${this.getPropertyValue(
                        pcaseloan,
                        'cCaseRejReason',
                        'N/A'
                    )}`,
                ],
            ],
        });

        currentY = (doc as any).lastAutoTable.finalY + 6;

        // Credit Info Section
        currentY = this.checkPageBreak(doc, currentY, margins, 50); // Check if we need a new page
        currentY = this.addSectionHeader(doc, 'Credit Info', currentY);
        autoTable(doc, {
            startY: currentY,
            theme: 'plain',
            margin: { left: margins.left, right: margins.right },
            styles: {
                fontSize: 10,
                cellPadding: 1,
                lineWidth: 0,
            },
            columnStyles: {
                0: { cellWidth: 95 },
                1: { cellWidth: 95 },
            },
            body: [
                [
                    `ANNUAL SALES TURNOVER (RM): ${this.formatCurrency(
                        this.getPropertyValue(
                            pcaseloan,
                            'cCaseSalesTurnover',
                            '-'
                        )
                    )}`,
                    `NET OPERATING INCOME (RM): ${this.formatCurrency(
                        this.getPropertyValue(pcaseloan, 'cCaseNetIncome', '-')
                    )}`,
                ],
                [
                    `PAID UP CAPITAL (RM): ${this.formatCurrency(
                        this.getPropertyValue(
                            pcaseloan,
                            'cCasePaidupCapital',
                            '-'
                        )
                    )}`,
                    `EXISTING MONTHLY LOAN INSTALLMENTS (RM): ${this.formatCurrency(
                        this.getPropertyValue(pcaseloan, 'cCaseMthLnInst', '-')
                    )}`,
                ],
                [
                    `PROFITABILITY TREND: ${this.getProfitabilityLabel(
                        pcaseloan
                    )}`,
                    '',
                ],
            ],
        });

        // Save the PDF
        const fileName = `application-case-${caseId}-${moment().format(
            'YYYYMMDDHHmmSS'
        )}.pdf`;
        doc.save(fileName);
    }

    private addSectionHeader(doc: jsPDF, title: string, y: number): number {
        const margins = { left: 15 }; // Use same margin as defined in generatePdf
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(title, margins.left, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        return y + 6;
    }

    private checkPageBreak(
        doc: jsPDF,
        currentY: number,
        margins: any,
        requiredSpace: number
    ): number {
        const pageHeight = doc.internal.pageSize.getHeight();
        const availableSpace = pageHeight - margins.bottom - currentY;

        if (availableSpace < requiredSpace) {
            doc.addPage();
            return margins.top;
        }

        return currentY;
    }

    private getPropertyValue(
        obj: any,
        path: string,
        defaultValue: any = '-'
    ): any {
        const value =
            obj && obj[path] !== undefined && obj[path] !== null
                ? obj[path]
                : defaultValue;
        return value === '' ? defaultValue : value;
    }

    private formatCurrency(value: any): string {
        if (!value || value === '-' || isNaN(Number(value))) {
            return '-';
        }
        return new Intl.NumberFormat('en-MY', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value));
    }

    private capitalizeFirst(str: string): string {
        if (!str || str === '-') return str;
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    private getMappedLabel(inputName: string, value: any): string {
        if (!value || value === '-') return '-';

        const dataItem = PDFData.find((item) => item.inputName === inputName);
        if (!dataItem) {
            return value;
        }

        // Special handling for business sector - pad single digits with leading zero
        let searchValue = value;
        if (inputName === 'bizSector' && value && !isNaN(Number(value))) {
            searchValue = String(value).padStart(2, '0');
        }

        // Convert value to string for comparison and handle different types
        const stringValue = String(searchValue);
        const option = dataItem.inputOptions.find(
            (opt) =>
                String(opt.value) === stringValue ||
                opt.value === searchValue ||
                (typeof opt.value === 'number' &&
                    Number(stringValue) === opt.value)
        );

        if (!option) {
            return value;
        }

        return option.label;
    }

    private getProfitabilityLabel(pcaseloan: any): string {
        const bizOpYear = this.getPropertyValue(pcaseloan, 'cCaseBizOpYear');
        const bizProfit = this.getPropertyValue(pcaseloan, 'cCaseBizProfit');

        // Handle cases where business is less than 1 year old
        if (bizOpYear === 0.5 || bizOpYear === 0.6) {
            if (!bizProfit) return 'N/A';
        }

        // Handle non-profitable cases
        if (!bizProfit && bizOpYear >= 1) {
            return 'Non Profitable';
        }

        if (bizProfit && bizProfit.split('')[1] === '0') {
            return 'Non Profitable';
        }

        // Map profitable cases
        return this.getMappedLabel('profitabilityRecentYear', bizProfit) || '-';
    }

    downloadFeedbackReport(data: any[]): void {
        if (!data || !data.length) {
            console.error('No data provided for PDF generation');
            return;
        }

        const pdf: any = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'a4',
        });

        pdf.setFontSize(16);
        pdf.text(
            this.translate.instant('JC3.JC3_FEEDBACK_FORM') ||
                'User Feedback Report',
            40,
            40
        );

        const headers = [
            [
                this.translate.instant('JC3.NAME') || 'Name',
                this.translate.instant('JC3.EMAIL') || 'Email',
                this.translate.instant('JC3.COMPANY_INSTITUTION_NAME') ||
                    'Company Name',
                this.translate.instant('JC3.TYPE_OF_INDUSTRY') || 'Industry',
                this.translate.instant('JC3.FEEDBACK_SUGGESTION') || 'Feedback',
                this.translate.instant('JC3.DATE') || 'Created At',
            ],
        ];

        const rows = data.map((item) => [
            item.name,
            item.email,
            item.companyName,
            item.typeOfIndustry,
            item.feedback,
            this.datePipe.transform(item.createdAt, 'dd MMM yyyy HH:mm:ss'),
        ]);

        autoTable(pdf, {
            startY: 60,
            head: headers,
            body: rows,
            styles: {
                fontSize: 8.5,
                cellPadding: 4,
                valign: 'middle',
                halign: 'center', // 👈 center align all content
                lineColor: [200, 200, 200],
                lineWidth: 0.5,
                overflow: 'linebreak',
                cellWidth: 'wrap',
            },
            headStyles: {
                fillColor: false,
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                halign: 'center', // 👈 also center header
                valign: 'middle',
            },
            columnStyles: {
                0: { cellWidth: 70, halign: 'center' },
                1: { cellWidth: 100, halign: 'center' },
                2: { cellWidth: 80, halign: 'center' },
                3: { cellWidth: 70, halign: 'center' },
                4: { cellWidth: 'auto', halign: 'center' }, // feedback flexible
                5: { cellWidth: 90, halign: 'center' },
            },
            margin: { left: 30, right: 30 },
            didDrawPage: (dataArg) => {
                pdf.setFontSize(9);
                const page = pdf.internal.getNumberOfPages();
                pdf.text(
                    `Page ${page}`,
                    pdf.internal.pageSize.getWidth() - 80,
                    pdf.internal.pageSize.getHeight() - 20
                );
            },
        });

        pdf.save('Feedback_Report.pdf');
    }
}
